// frontend/src/pages/AdminStories.js – Admin: Storylines panel
// Build: 2026-01-10-01 (Pagination + color pills)
//
// Admin-only moderation for storylines:
// - List all storylines (admin endpoint, paginated)
// - Search (filters current page)
// - Set status: active | archived
// - Set visibility: public | members | private
// - Delete storyline

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";
import { useUser } from "../UserContext";

function safeText(v) {
  return String(v || "").trim();
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function getStoryId(s) {
  return String(s?.id || s?._id || "").trim();
}

function scenesCount(s) {
  if (typeof s?.scenesCount === "number") return s.scenesCount;
  if (Array.isArray(s?.scenes)) return s.scenes.length;
  return 0;
}

function authorLabel(s) {
  const name = safeText(s?.authorName);
  const un = safeText(s?.authorUsername);
  const legacyA = safeText(s?.author);
  const legacyC = safeText(s?.createdBy);

  if (name) return name;
  if (un) return `@${un}`;
  if (legacyA) return legacyA;
  if (legacyC) return legacyC;
  return "Unknown";
}

function clampInt(n, lo, hi) {
  const x = Number(n);
  if (!Number.isFinite(x)) return lo;
  return Math.max(lo, Math.min(hi, Math.trunc(x)));
}

function emptyPublicationCorrectionForm() {
  return {
    publicationStatus: "inDevelopment",
    visibilityStatus: "public_preview",
    reason: "",
    publicationTitle: "",
    publicationSubtitle: "",
    publishedAuthorName: "",
    publicationUrl: "",
    publicationCoverUrl: "",
  };
}

function publicationStatusLabel(value) {
  const status = safeText(value);

  if (status === "publishedAmazon") return "Published on Amazon";
  if (status === "publishedExternal") return "Published externally";
  if (status === "completed") return "Completed";
  return "In Development";
}

function pillStyle(kind) {
  // Keep it simple and readable
  const base = {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid rgba(15, 61, 46, 0.18)",
    background: "rgba(16, 185, 129, 0.08)",
    color: "#0f3d2e",
    whiteSpace: "nowrap",
  };

  const k = String(kind || "").toLowerCase();

  if (k === "archived") {
    return { ...base, background: "rgba(148, 163, 184, 0.18)", color: "#334155" };
  }
  if (k === "private") {
    return { ...base, background: "rgba(239, 68, 68, 0.10)", color: "#7f1d1d" };
  }
  if (k === "members") {
    return { ...base, background: "rgba(245, 158, 11, 0.12)", color: "#7c2d12" };
  }
  if (k === "public") {
    return { ...base, background: "rgba(16, 185, 129, 0.10)", color: "#0f3d2e" };
  }
  if (k === "active") {
    return { ...base, background: "rgba(34, 197, 94, 0.10)", color: "#14532d" };
  }

  return base;
}

export default function AdminStories({ onGo }) {
  const { isAdmin } = useUser();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [stories, setStories] = useState([]);
  const [q, setQ] = useState("");
  const [correctionStoryId, setCorrectionStoryId] = useState("");
  const [correctionForm, setCorrectionForm] = useState(
    emptyPublicationCorrectionForm
  );

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  async function load(p = page, l = limit) {
    setErr("");
    setOk("");
    setBusy(true);

    const pageSafe = clampInt(p, 1, 1000000);
    const limitSafe = clampInt(l, 5, 100);

    try {
      const data = await api(`/api/admin/stories?page=${pageSafe}&limit=${limitSafe}`, { method: "GET" });

      if (data?.ok) {
        const list = Array.isArray(data.stories)
          ? data.stories
          : Array.isArray(data.items)
          ? data.items
          : [];

        setStories(list);

        const tp = clampInt(data?.totalPages, 1, 1000000);
        const ti = clampInt(data?.totalItems, 0, 1000000000);

        setTotalPages(tp);
        setTotalItems(ti);

        // In case backend clamps page, keep UI sane
        setPage(clampInt(data?.page || pageSafe, 1, tp));
        setLimit(clampInt(data?.limit || limitSafe, 5, 100));
      } else {
        setStories([]);
        setTotalPages(1);
        setTotalItems(0);
        setErr(data?.message || "Failed to load storylines.");
      }
    } catch (e) {
      setStories([]);
      setTotalPages(1);
      setTotalItems(0);
      setErr(e?.message || "Failed to load storylines.");
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(id, status) {
    const sid = String(id || "").trim();
    if (!sid) return;

    const st = String(status || "").trim().toLowerCase();
    if (st !== "active" && st !== "archived") return;

    setErr("");
    setOk("");
    setBusy(true);
    try {
      const data = await api(`/api/admin/stories/${encodeURIComponent(sid)}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: st }),
      });
      if (!data?.ok) throw new Error(data?.message || "Status update failed.");

      const story = data?.story || null;
      if (story) {
        setStories((prev) => prev.map((s) => (getStoryId(s) === sid ? story : s)));
      }
      setOk(`Updated status: ${sid} → ${st}`);
    } catch (e) {
      setErr(e?.message || "Status update failed.");
    } finally {
      setBusy(false);
    }
  }

  async function setVisibility(id, visibility) {
    const sid = String(id || "").trim();
    if (!sid) return;

    const v = String(visibility || "").trim().toLowerCase();
    if (v !== "public" && v !== "members" && v !== "private") return;

    setErr("");
    setOk("");
    setBusy(true);
    try {
      const data = await api(`/api/admin/stories/${encodeURIComponent(sid)}/visibility`, {
        method: "PATCH",
        body: JSON.stringify({ visibility: v }),
      });
      if (!data?.ok) throw new Error(data?.message || "Visibility update failed.");

      const story = data?.story || null;
      if (story) {
        setStories((prev) => prev.map((s) => (getStoryId(s) === sid ? story : s)));
      }
      setOk(`Updated visibility: ${sid} → ${v}`);
    } catch (e) {
      setErr(e?.message || "Visibility update failed.");
    } finally {
      setBusy(false);
    }
  }

  function openPublicationCorrection(story) {
    const sid = getStoryId(story);
    if (!sid) return;

    setErr("");
    setOk("");
    setCorrectionStoryId(sid);
    setCorrectionForm({
      publicationStatus:
        safeText(story?.publicationStatus) || "inDevelopment",
      visibilityStatus:
        safeText(story?.visibilityStatus) || "public_preview",
      reason: "",
      publicationTitle: safeText(story?.publicationTitle),
      publicationSubtitle: safeText(story?.publicationSubtitle),
      publishedAuthorName:
        safeText(story?.publishedAuthorName) || authorLabel(story),
      publicationUrl: safeText(story?.publicationUrl),
      publicationCoverUrl: safeText(story?.publicationCoverUrl),
    });
  }

  function closePublicationCorrection() {
    if (busy) return;
    setCorrectionStoryId("");
    setCorrectionForm(emptyPublicationCorrectionForm());
  }

  function updateCorrectionField(name, value) {
    setCorrectionForm((current) => {
      const next = {
        ...current,
        [name]: value,
      };

      if (name === "publicationStatus") {
        if (
          value === "publishedAmazon" ||
          value === "publishedExternal"
        ) {
          next.visibilityStatus = "published";
        } else if (current.visibilityStatus === "published") {
          next.visibilityStatus = "public_preview";
        }
      }

      return next;
    });
  }

  async function submitPublicationCorrection(event) {
    event.preventDefault();

    const sid = safeText(correctionStoryId);
    if (!sid) return;

    const reason = safeText(correctionForm.reason);
    if (reason.length < 10) {
      setErr("Please provide a correction reason of at least 10 characters.");
      return;
    }

    setErr("");
    setOk("");
    setBusy(true);

    try {
      const data = await api(
        `/api/admin/stories/${encodeURIComponent(
          sid
        )}/publication-correction`,
        {
          method: "PATCH",
          body: JSON.stringify({
            publicationStatus: correctionForm.publicationStatus,
            visibilityStatus: correctionForm.visibilityStatus,
            reason,
            publicationTitle: correctionForm.publicationTitle,
            publicationSubtitle: correctionForm.publicationSubtitle,
            publishedAuthorName: correctionForm.publishedAuthorName,
            publicationUrl: correctionForm.publicationUrl,
            publicationCoverUrl: correctionForm.publicationCoverUrl,
          }),
        }
      );

      if (!data?.ok) {
        throw new Error(
          data?.message || "Publication correction failed."
        );
      }

      const correctedStory = data?.story || null;
      if (correctedStory) {
        setStories((current) =>
          current.map((story) =>
            getStoryId(story) === sid ? correctedStory : story
          )
        );
      }

      setOk(
        `Publication status corrected: ${
          safeText(correctedStory?.title) || sid
        } → ${publicationStatusLabel(
          correctedStory?.publicationStatus ||
            correctionForm.publicationStatus
        )}`
      );

      setCorrectionStoryId("");
      setCorrectionForm(emptyPublicationCorrectionForm());
    } catch (e) {
      setErr(e?.message || "Publication correction failed.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteStory(id) {
    const sid = String(id || "").trim();
    if (!sid) return;

    setErr("");
    setOk("");

    const sure = window.confirm("Archive this storyline?\nIt will be hidden and made private, but not permanently deleted.");
    if (!sure) return;

    setBusy(true);
    try {
      const data = await api(`/api/admin/stories/${encodeURIComponent(sid)}`, { method: "DELETE" });
      if (!data?.ok) throw new Error(data?.message || "Archive failed.");

      setStories((prev) =>
        prev.map((s) =>
          getStoryId(s) === sid
            ? {
                ...s,
                status: "archived",
                visibility: "private",
                visibilityStatus: "private_draft",
              }
            : s
        )
      );
      setOk(`Archived storyline: ${sid}`);
    } catch (e) {
      setErr(e?.message || "Archive failed.");
    } finally {
      setBusy(false);
    }
  }

  const filtered = useMemo(() => {
    const s = safeText(q).toLowerCase();
    if (!s) return stories;

    return (Array.isArray(stories) ? stories : []).filter((x) => {
      const bag = [
        x?.title,
        x?.forum,
        x?.authorUsername,
        x?.authorName,
        x?.author,
        x?.createdBy,
        x?.status,
        x?.visibility,
        x?.visibilityStatus,
        x?.publicationStatus,
        x?.publicationTitle,
        x?.publishedAuthorName,
        x?.id,
        x?._id,
      ]
        .map((v) => safeText(v).toLowerCase())
        .join(" ");

      return bag.includes(s);
    });
  }, [stories, q]);

  useEffect(() => {
    if (!isAdmin) return;
    setPage(1);
    setLimit(20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    load(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, page, limit]);

  if (!isAdmin) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 820, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>Admin: Storylines</h2>
          <p style={{ color: "#475569" }}>Forbidden.</p>
          {typeof onGo === "function" ? (
            <button className="btn" type="button" onClick={() => onGo("home")}>
              Go Home
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const pageLabel = `Page ${page} of ${totalPages} • ${totalItems} total`;
  const correctionStory =
    stories.find(
      (story) => getStoryId(story) === correctionStoryId
    ) || null;

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 1180, margin: "0 auto" }}>
        {/* Soft admin header bar */}
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 14,
            border: "1px solid rgba(15, 61, 46, 0.16)",
            background:
              "linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(255, 255, 255, 0.92))",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ marginTop: 0, marginBottom: 4, color: "#0f3d2e" }}>Admin: Storylines</h2>
              <p style={{ marginTop: 0, color: "#355b52", fontWeight: 600 }}>
                Moderate Storylines: status, visibility, publication corrections and archival.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button className="btn" type="button" onClick={() => load(page, limit)} disabled={busy}>
                {busy ? "Working..." : "Refresh"}
              </button>
              {typeof onGo === "function" ? (
                <button className="btn" type="button" onClick={() => onGo("admin")}>
                  Back to Dashboard
                </button>
              ) : null}
            </div>
          </div>

          {/* Pager */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button
                className="btn"
                type="button"
                disabled={busy || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>

              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f3d2e" }}>{pageLabel}</div>

              <button
                className="btn"
                type="button"
                disabled={busy || page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f3d2e" }}>Per page</div>
              <select
                value={limit}
                onChange={(e) => {
                  const v = clampInt(e.target.value, 5, 100);
                  setPage(1);
                  setLimit(v);
                }}
                style={{
                  padding: "8px 10px",
                  borderRadius: 12,
                  border: "1px solid rgba(15, 61, 46, 0.22)",
                  fontWeight: 800,
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, forum, author, status, visibility..."
            style={{ padding: 10, borderRadius: 12, border: "1px solid #e5e7eb", minWidth: 320, width: "100%" }}
          />
        </div>

        {err ? (
          <div className="banner" style={{ marginTop: 12, borderLeft: "6px solid #dc2626" }}>
            {err}
          </div>
        ) : null}
        {ok ? (
          <div className="banner" style={{ marginTop: 12, borderLeft: "6px solid #16a34a" }}>
            {ok}
          </div>
        ) : null}

        {correctionStory ? (
          <form
            onSubmit={submitPublicationCorrection}
            style={{
              marginTop: 14,
              padding: 16,
              borderRadius: 14,
              border: "1px solid rgba(15, 61, 46, 0.22)",
              background: "rgba(248, 250, 252, 0.96)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3 style={{ margin: 0, color: "#0f3d2e" }}>
                  Correct Publication Details
                </h3>
                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 800,
                    color: "#334155",
                  }}
                >
                  {safeText(correctionStory.title) || "Untitled Storyline"}
                </div>
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 12,
                    color: "#64748b",
                  }}
                >
                  Current status:{" "}
                  {publicationStatusLabel(
                    correctionStory.publicationStatus
                  )}
                </div>
              </div>

              <button
                className="btn"
                type="button"
                disabled={busy}
                onClick={closePublicationCorrection}
              >
                Close
              </button>
            </div>

            <div
              style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 12,
              }}
            >
              <label style={{ fontWeight: 800, color: "#334155" }}>
                Publication status
                <select
                  value={correctionForm.publicationStatus}
                  onChange={(event) =>
                    updateCorrectionField(
                      "publicationStatus",
                      event.target.value
                    )
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <option value="inDevelopment">
                    In Development
                  </option>
                  <option value="completed">Completed</option>
                  <option value="publishedAmazon">
                    Published on Amazon
                  </option>
                  <option value="publishedExternal">
                    Published externally
                  </option>
                </select>
              </label>

              <label style={{ fontWeight: 800, color: "#334155" }}>
                Visibility
                <select
                  value={correctionForm.visibilityStatus}
                  onChange={(event) =>
                    updateCorrectionField(
                      "visibilityStatus",
                      event.target.value
                    )
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <option value="private_draft">
                    Private draft
                  </option>
                  <option value="nextscenes_readers">
                    NextScenes Readers
                  </option>
                  <option value="public_preview">
                    Public preview
                  </option>
                  <option value="published">Published</option>
                </select>
              </label>

              <label style={{ fontWeight: 800, color: "#334155" }}>
                Official publication title
                <input
                  value={correctionForm.publicationTitle}
                  onChange={(event) =>
                    updateCorrectionField(
                      "publicationTitle",
                      event.target.value
                    )
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <label style={{ fontWeight: 800, color: "#334155" }}>
                Subtitle
                <input
                  value={correctionForm.publicationSubtitle}
                  onChange={(event) =>
                    updateCorrectionField(
                      "publicationSubtitle",
                      event.target.value
                    )
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <label style={{ fontWeight: 800, color: "#334155" }}>
                Published author
                <input
                  value={correctionForm.publishedAuthorName}
                  onChange={(event) =>
                    updateCorrectionField(
                      "publishedAuthorName",
                      event.target.value
                    )
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <label style={{ fontWeight: 800, color: "#334155" }}>
                Publication link
                <input
                  type="url"
                  value={correctionForm.publicationUrl}
                  onChange={(event) =>
                    updateCorrectionField(
                      "publicationUrl",
                      event.target.value
                    )
                  }
                  placeholder="https://..."
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <label style={{ fontWeight: 800, color: "#334155" }}>
                Final cover URL
                <input
                  value={correctionForm.publicationCoverUrl}
                  onChange={(event) =>
                    updateCorrectionField(
                      "publicationCoverUrl",
                      event.target.value
                    )
                  }
                  placeholder="/images/covers/... or https://..."
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 5,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                    boxSizing: "border-box",
                  }}
                />
              </label>
            </div>

            <label
              style={{
                display: "block",
                marginTop: 12,
                fontWeight: 800,
                color: "#334155",
              }}
            >
              Correction reason
              <textarea
                required
                minLength={10}
                rows={3}
                value={correctionForm.reason}
                onChange={(event) =>
                  updateCorrectionField(
                    "reason",
                    event.target.value
                  )
                }
                placeholder="Explain why the administrator is correcting the writer’s selection."
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 5,
                  padding: 10,
                  borderRadius: 10,
                  border: "1px solid #cbd5e1",
                  boxSizing: "border-box",
                  resize: "vertical",
                }}
              />
            </label>

            <div
              style={{
                marginTop: 12,
                fontSize: 12,
                color: "#64748b",
              }}
            >
              This correction will be recorded in Story history and the
              Writer will be notified. It does not delete scenes,
              authorship or project history.
            </div>

            <div
              style={{
                marginTop: 14,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                className="btn"
                type="submit"
                disabled={
                  busy || safeText(correctionForm.reason).length < 10
                }
              >
                {busy ? "Saving correction..." : "Save correction"}
              </button>

              <button
                className="btn"
                type="button"
                disabled={busy}
                onClick={closePublicationCorrection}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        <div style={{ marginTop: 14, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", background: "rgba(16, 185, 129, 0.08)" }}>
                <th style={{ padding: 10 }}>Storyline</th>
                <th style={{ padding: 10 }}>Forum</th>
                <th style={{ padding: 10 }}>Author</th>
                <th style={{ padding: 10 }}>Status</th>
                <th style={{ padding: 10 }}>Visibility</th>
                <th style={{ padding: 10 }}>Updated</th>
                <th style={{ padding: 10 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((s, idx) => {
                const id = getStoryId(s);
                const title = safeText(s?.title) || "Untitled";
                const forum = safeText(s?.forum) || "Unclassified Forum";
                const author = authorLabel(s);
                const status = safeText(s?.status) || "active";
                const visibility = safeText(s?.visibility) || "public";
                const updated = fmtDate(s?.updatedAt) || fmtDate(s?.createdAt) || "";
                const count = scenesCount(s);

                const rowKey = id || `${title}_${idx}`;

                return (
                  <tr key={rowKey} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: 10 }}>
                      <div style={{ fontWeight: 900, color: "#0f172a" }}>{title}</div>
                      <div style={{ marginTop: 2, fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                        Scenes: {count} · ID: {id || "(missing)"}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <span style={pillStyle(s?.publicationStatus)}>
                          {publicationStatusLabel(s?.publicationStatus)}
                        </span>
                      </div>
                    </td>

                    <td style={{ padding: 10 }}>{forum}</td>
                    <td style={{ padding: 10 }}>{author}</td>
                    <td style={{ padding: 10 }}>
                      <span style={pillStyle(status)}>{status}</span>
                    </td>
                    <td style={{ padding: 10 }}>
                      <span style={pillStyle(visibility)}>{visibility}</span>
                    </td>
                    <td style={{ padding: 10 }}>{updated}</td>

                    <td style={{ padding: 10 }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button className="btn" type="button" disabled={busy || !id} onClick={() => setStatus(id, "active")}>
                          activate
                        </button>
                        <button className="btn" type="button" disabled={busy || !id} onClick={() => setStatus(id, "archived")}>
                          archive
                        </button>

                        <button className="btn" type="button" disabled={busy || !id} onClick={() => setVisibility(id, "public")}>
                          public
                        </button>
                        <button className="btn" type="button" disabled={busy || !id} onClick={() => setVisibility(id, "members")}>
                          members
                        </button>
                        <button className="btn" type="button" disabled={busy || !id} onClick={() => setVisibility(id, "private")}>
                          private
                        </button>

                        <button
                          className="btn"
                          type="button"
                          disabled={busy || !id}
                          onClick={() => openPublicationCorrection(s)}
                        >
                          publication
                        </button>

                        <button className="btn btn-danger" type="button" disabled={busy || !id} onClick={() => deleteStory(id)}>
                          archive
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!busy && filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 12, color: "#64748b" }}>
                    No storylines.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
          Tip: “archive” is for hiding. “private” is for locking it away.
        </div>
      </div>
    </div>
  );
}
